/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "fabric",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Service principal creation",
          "depth": 0,
          "id": "service-principal-creation"
        }, {
          "value": "Client secret",
          "depth": 1,
          "id": "client-secret"
        }, {
          "value": "Certificate",
          "depth": 1,
          "id": "certificate"
        }, {
          "value": "Fabric configuration",
          "depth": 0,
          "id": "fabric-configuration"
        }, {
          "value": "Attaching your service principal to your Fabric warehouse",
          "depth": 0,
          "id": "attaching-your-service-principal-to-your-fabric-warehouse"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Choose your sync engine",
          "depth": 1,
          "id": "choose-your-sync-engine"
        }, {
          "value": "Provide credentials",
          "depth": 1,
          "id": "provide-credentials"
        }, {
          "value": "Test your connection",
          "depth": 1,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      code: "code",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {SyncEngineSnippet, EngineComparisonTable} = _components;
    if (!EngineComparisonTable) _missingMdxReference("EngineComparisonTable", true);
    if (!SyncEngineSnippet) _missingMdxReference("SyncEngineSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in Microsoft Fabric and push it to downstream destinations. Connecting Hightouch to Microsoft Fabric requires some setup in both platforms. In Microsoft Fabric, you need to create a service principal and grant it access to your Fabric resource."
      }), "\n", _jsx(_components.h2, {
        id: "service-principal-creation",
        children: "Service principal creation"
      }), "\n", _jsx(_components.p, {
        children: "First, we need to create a service principal. These are the credentials Hightouch will use to access your Microsoft Fabric resources."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Sign in to the ", _jsx(_components.a, {
            href: "https://entra.microsoft.com/",
            children: "Microsoft Entra admin center"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the sidebar, select ", _jsx(_components.strong, {
            children: "Identity"
          }), " > ", _jsx(_components.strong, {
            children: "Applications"
          }), " > ", _jsx(_components.strong, {
            children: "App registrations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "New registration"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Pick a name for your application and select ", _jsx(_components.strong, {
            children: "Accounts in this organizational directory only"
          }), " as the supported account type."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Register"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Note your newly created ", _jsx(_components.strong, {
            children: "Application (client) ID"
          }), " and ", _jsx(_components.strong, {
            children: "Directory (tenant) ID"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add a certificate or secret"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["From here, you can opt to use either a ", _jsx(_components.strong, {
          children: "Client secret"
        }), " or a ", _jsx(_components.strong, {
          children: "Certificate"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "client-secret",
        children: "Client secret"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Client secrets"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "New client secret"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Name your client secret and select its expiration date. To maintain access to Fabric, make sure to refresh your client secret prior to its expiration."
        }), "\n", _jsxs(_components.li, {
          children: ["Note your client secret ", _jsx(_components.strong, {
            children: "Value"
          }), " now as it cannot be retrieved later. It will be used to connect Hightouch to Microsoft Fabric."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "certificate",
        children: "Certificate"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Certificates"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Upload certificate"
          }), " and upload a valid signed certificate."]
        }), "\n", _jsxs(_components.li, {
          children: ["Note your certificate's ", _jsx(_components.strong, {
            children: "Thumbprint"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more detailed instructions on service principal creation, you can check out ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/entra/identity-platform/howto-create-service-principal-portal",
          children: "Microsoft's documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "fabric-configuration",
        children: "Fabric configuration"
      }), "\n", _jsx(_components.p, {
        children: "By default, Microsoft Fabric may not be accessible via service principal. To connect, you'll need to take the following steps."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Log in to ", _jsx(_components.a, {
            href: "https://app.fabric.microsoft.com/",
            children: "Microsoft Fabric"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Synapse Data Engineering"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the top right of the window, select ", _jsx(_components.strong, {
            children: "Settings"
          }), " > ", _jsx(_components.strong, {
            children: "Governance and insights"
          }), " > ", _jsx(_components.strong, {
            children: "Admin portal"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the sidebar, select ", _jsx(_components.strong, {
            children: "Tenant settings"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Scroll down to ", _jsx(_components.strong, {
            children: "Developer settings"
          }), " and select ", _jsx(_components.strong, {
            children: "Service principals can use Fabric APIs"
          }), ". Make sure the toggle is set to ", _jsx(_components.strong, {
            children: "Enabled"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Next go to ", _jsx(_components.strong, {
            children: "OneLake settings"
          }), " and select ", _jsx(_components.strong, {
            children: "Users can access data stored in OneLake with apps external to Fabric"
          }), ". This toggle should be ", _jsx(_components.strong, {
            children: "Enabled"
          }), " as well."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "attaching-your-service-principal-to-your-fabric-warehouse",
        children: "Attaching your service principal to your Fabric warehouse"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Log in to ", _jsx(_components.a, {
            href: "https://app.fabric.microsoft.com/",
            children: "Microsoft Fabric"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the workspace that contains your warehouse."
        }), "\n", _jsx(_components.li, {
          children: "Hover over your warehouse and select the three dots menu."
        }), "\n", _jsxs(_components.li, {
          children: ["Choose ", _jsx(_components.strong, {
            children: "Manage permissions"
          }), " from the menu."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the next page, select ", _jsx(_components.strong, {
            children: "Add user"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Type in the name of your service principal and check the box for ", _jsx(_components.strong, {
            children: "Read all data using SQL (ReadData)"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Grant"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Microsoft Fabric"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your Microsoft Fabric instance. If using the hostname, make sure to only insert the hostname and not the full URL (remove the leading ", _jsx(_components.code, {
            children: "http://"
          }), " and the final ", _jsx(_components.code, {
            children: "/"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your Microsoft Fabric instance. The default is 1433, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": The name of the database in Microsoft Fabric."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Optionally you can enter a ", _jsx(_components.strong, {
          children: "Request timeout duration"
        }), " and whether to ", _jsx(_components.strong, {
          children: "Trust server certification"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-sync-engine",
        children: "Choose your sync engine"
      }), "\n", _jsx(SyncEngineSnippet, {
        source: "Fabric"
      }), "\n", _jsx(_components.h4, {
        id: "basic-versus-lightning-engine-comparison",
        children: "Basic versus Lightning engine comparison"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), " requires granting write access to your data warehouse, which makes its setup more involved than the Basic sync engine. However, it is more performant and reliable than the Basic engine. This makes it the ideal choice to guarantee faster syncs, especially with large data models. It also supports more features, such as ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), ", ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), ", and ", _jsx(_components.a, {
          href: "https://hightouch.com/platform/identity-resolution",
          children: "Identity Resolution"
        }), "."]
      }), "\n", _jsx(EngineComparisonTable, {
        source: "Fabric"
      }), "\n", _jsx(_components.h4, {
        id: "lightning-engine-setup",
        children: "Lightning engine setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To set up the Lightning engine, run the following commands. Make sure to replace ", _jsx(_components.strong, {
          children: "your_service_principal_or_user"
        }), " with the name of the service principal created earlier."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " (", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " sys.schemas ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " name ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'hightouch_audit'"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BEGIN"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXEC"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'CREATE SCHEMA [hightouch_audit]'"
          }), ");\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "END"
          }), ";\nIF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " (", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " sys.schemas ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " name ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'hightouch_planner'"
          }), ")\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BEGIN"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXEC"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'CREATE SCHEMA [hightouch_planner]'"
          }), ");\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "END"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " CONTROL ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA::hightouch_audit ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " [your_service_principal_or_user];\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " CONTROL ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA::hightouch_planner ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " [your_service_principal_or_user];\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "provide-credentials",
        children: "Provide credentials"
      }), "\n", _jsx(_components.p, {
        children: "Then you'll enter the credentials from the previous service principal creation step"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Tenant ID"
          }), " (or Directory ID)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client ID"
          }), " (or Application ID)"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you chose to create a ", _jsx(_components.strong, {
          children: "Client Secret"
        }), " for your service principal, you'll enter it here."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you instead chose to create a ", _jsx(_components.strong, {
          children: "Certificate"
        }), ", enter in your certificate's ", _jsx(_components.strong, {
          children: "Thumbprint"
        }), " and the associated ", _jsx(_components.strong, {
          children: "private key"
        }), ". Make sure that the private key is in PEM format and includes the header and footer."]
      }), "\n", _jsx(_components.h3, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsxs(_components.p, {
        children: ["When setting up Microsoft Fabric as a source for the first time, Hightouch validates your credentials and access to your database. Once the test passes, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " to finish setup."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, you've completed setting up Microsoft Fabric as a source. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Microsoft Fabric."]
      }), "\n", _jsx(_components.p, {
        children: "The Microsoft Fabric source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["A Microsoft Fabric source can also read other lakehouses and warehouses in the same workspace as the warehouse it connects to. This includes data from other workspaces that you've added to a lakehouse as ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/fabric/onelake/onelake-shortcuts",
          children: "OneLake shortcuts"
        }), ". These tables appear in the table selector. In SQL, reference them with three-part names (", _jsx(_components.code, {
          children: "database.schema.table"
        }), "), as described in Microsoft's docs on ", _jsx(_components.a, {
          href: "https://learn.microsoft.com/en-us/fabric/data-warehouse/query-warehouse#write-a-cross-database-query",
          children: "cross-database queries"
        }), ". Your service principal needs read access to each lakehouse or warehouse you query."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use Microsoft Fabric as an event storage destination for Hightouch Events. That connection needs different grants. See ", _jsx(_components.a, {
          href: "/events/warehouses/warehouse#microsoft-fabric",
          children: "Microsoft Fabric prerequisites"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.p, {
        children: "Most issues with the Microsoft Fabric source can be resolved by verifying that your Microsoft Fabric configuration is correct. It must allow external connections via service principal."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
